/**
 * `SortControl` (DNKADM-0430 P1-D): the sort for a list with no column headers (card
 * rows). A compact "Sort by" select plus a direction toggle, for the `ListToolbar`'s
 * `filters` or `end` slot.
 *
 * It speaks the same `SortState` as {@link SortableHeader}, so a screen keeps its sort in
 * the URL with the same `parseSort` / `toSortParams` helpers from `./listSort` and sorts its
 * rows with `sortRows` (blanks last in both directions). Presentational only: the screen
 * owns the state.
 *
 * - Picking a different key sorts it in that option's `firstDir` (ascending when unset).
 *   A date such as "Last invited" reads best newest first, so it can start descending.
 * - The toggle flips the direction of the current key. Its arrow shows the current
 *   direction; its label says what a click does.
 */
import { MenuItem, Stack, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import type { SortDir, SortState } from './listSort';

export interface SortControlOption<K extends string> {
  value: K;
  label: string;
  /** The direction this key starts in when picked. Defaults to ascending. */
  firstDir?: SortDir;
}

export interface SortControlProps<K extends string> {
  sort: SortState<K>;
  options: ReadonlyArray<SortControlOption<K>>;
  onChange: (next: SortState<K>) => void;
  /** The select's label. Defaults to "Sort by". */
  label?: string;
  disabled?: boolean;
}

export function SortControl<K extends string>({
  sort,
  options,
  onChange,
  label = 'Sort by',
  disabled,
}: SortControlProps<K>) {
  const t = resolveTokens(useTheme());
  const ascending = sort.dir === 'asc';

  const pickKey = (raw: string) => {
    const option = options.find((o) => o.value === raw);
    if (option === undefined || option.value === sort.key) return;
    onChange({ key: option.value, dir: option.firstDir ?? 'asc' });
  };

  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
      <TextField
        select
        size="small"
        label={label}
        value={sort.key}
        onChange={(e) => pickKey(e.target.value)}
        disabled={disabled}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ minWidth: 160, bgcolor: t.surface }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <IconAction
        label={
          ascending ? 'Ascending, click to sort descending' : 'Descending, click to sort ascending'
        }
        size="small"
        disabled={disabled}
        onClick={() => onChange({ key: sort.key, dir: ascending ? 'desc' : 'asc' })}
      >
        <Icon name={ascending ? 'arrow_upward' : 'arrow_downward'} />
      </IconAction>
    </Stack>
  );
}
